import * as _ from 'lodash'
import {IBusinessObject, IBusinessObjectCreateLoadOptions} from '../PA.Core/PA.Core.IF.BO';
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic';
import { PAdbContext } from '../PA.Core/PAdbContext';
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_CompanyRow,
    WB_MasterDataSetRow,
    WB_Snapshot_TypeRow, WB_SnapshotTypeSubjectAreaRow, WB_SubjectAreaRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbSnapshotType } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Snapshot_Type,
    esWB_Subject_Area,
    esWB_Snapshot_Msar,
    esWB_MasterData_Set,
    esWB_Company
} from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver"
import { ASync } from "../PA.Core/Basic/ASync"
import { SnapshotTypeBOValidations } from "./SnapshotTypeBO.Validations";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {SnapshotTypeBOAudit} from "./SnapshotTypeBO.Audit";
import {AuditService} from "../PA.Core/Security/AuditService";

export interface ISnapshotTypeLoadOptions {
    dbContext?: PAdbContext
}

const SnapshotTypeLoadOptions_default: ISnapshotTypeLoadOptions = {}

export class SnapshotTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_SnapshotType: WB_Snapshot_TypeRow;

    constructor(options?: IBusinessObjectCreateLoadOptions) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: 'New Snapshot Type' });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_SnapshotType = undefined;
    }

    //</editor-fold>

    get WB_SnapshotType(): WB_Snapshot_TypeRow {
        return this.drWB_SnapshotType;
    }

    //<editor-fold desc = "IBusinessObject operations">

    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Snapshot Type");

        let internalOnComplete = self._OnLoadComplete = (async() => {
            try{
            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Snapshot Type.";
                self.LoadProgressProvider.exception(sError);
                return self;
            }

            // Create record using dbContext
           let newSnapshotTypeRow: WB_Snapshot_TypeRow=<WB_Snapshot_TypeRow>await self.dbContext.createRecordWithKey(esWB_Snapshot_Type)
                    newSnapshotTypeRow.setDefaults()
                    self.drWB_SnapshotType = newSnapshotTypeRow;
                    self.dbContext.add(esWB_Snapshot_Type, newSnapshotTypeRow)
                    self.Identity.setAll(self.WB_SnapshotType.cif.WB_SNAPSHOTM_ID, self.WB_SnapshotType.cif.SNAPSHOT_TYPE_ID, self.WB_SnapshotType.cif.SNAPSHOT_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                await self.applyCreateOptions()

                return self;
            } catch(err ) {

                self.LoadProgressProvider.exception(err)
                return self
            }  })()

       return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_SnapshotType.cif.SNAPSHOT_TYPE_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_SnapshotType.cif.SNAPSHOT_TYPE_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_SnapshotType.WB_MasterDataSetRow = mds
                self.WB_SnapshotType.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Company
        let wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId
        if (_.isNumber(wbMasterDataSet)) {
            let company: WB_CompanyRow = <any>await self.dbContext.find(esWB_Company, wbCompany)
            if (company) {
                self.WB_SnapshotType.WB_CompanyRow = company
                self.WB_SnapshotType.cif.WB_COMPANY_ID = wbCompany
            }
        }

    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Snapshot Type already loaded.")
                resolve(self); // "Snapshot Type already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, SnapshotTypeLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isUndefined(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isUndefined(self.dbContext)) {
                error("Unable to determine dbContext for Snapshot Type.")
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SnapshotTypeBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = SnapshotTypeBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aSnapshotTypeRow: any = recordResolver.get(esWB_Snapshot_Type, anId)

                    if (_.isEmpty(aSnapshotTypeRow)) {
                        error("Snapshot Type not Found");
                        return;
                    }

                    self.drWB_SnapshotType = aSnapshotTypeRow;
                    self.Identity.setAll(self.WB_SnapshotType.cif.WB_SNAPSHOTM_ID, self.WB_SnapshotType.cif.SNAPSHOT_TYPE_ID, self.WB_SnapshotType.cif.SNAPSHOT_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SnapshotType, 'WB_SNAPSHOTM_ID', 'SNAPSHOT_TYPE_ID', 'SNAPSHOT_TYPE_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(err)
                    error(err)
                    return;
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_SnapshotType.cif.SNAPSHOT_TYPE_NAME + "(" + self.WB_SnapshotType.cif.SNAPSHOT_TYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Snapshot Type.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            SnapshotTypeBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbSnapshotType = <IwbSnapshotType>self.drWB_SnapshotType;
        this.SaveProgressProvider.start("Saving " + wbSnapshotType.SNAPSHOT_TYPE_NAME + "(" + wbSnapshotType.SNAPSHOT_TYPE_ID + ")");
        let auditService: AuditService = undefined

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Snapshot Type.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }
            let bDone = false

            self.Validate({})
                .then((ignore) => {
                    if(!self.bIsValidated){
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                })
                .then((ignore) => {
                    if (bDone) return
                    return SnapshotTypeBOAudit.SnapshotTypeAuditBO(self)

                })
                .then((anAuditService) => {
                    if (bDone) return
                    auditService = anAuditService
                    return  saveFunction(esWB_Snapshot_Type, self.drWB_SnapshotType, {})
                }).then((ignored) => {
                if (bDone) return
                if (auditService) {
                    return auditService.Save(bBatch)
                }
            })
                .then((ignore) => {
                    if (bDone) return
                    let tasks: Promise<any>[]=[]
                    tasks.push(saveFunction(
                        esWB_Snapshot_Msar,
                        self.drWB_SnapshotType.WB_SnapshotTypeSubjectAreaRows
                    ))
                    tasks.push(saveFunction(esWB_MasterData_Set,self.drWB_SnapshotType.WB_MasterDataSetRow))
                    tasks.push(saveFunction(esWB_Company,self.drWB_SnapshotType.WB_CompanyRow))

                    return Promise.all(tasks)
                })
            .then(ignored => {
                if (bDone) return
                if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                  self._bIsSaved = true;
                  LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.Lease_Type_SnapshotM);
                });
              } else {
                self._bIsSaved = true;
                LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.Lease_Type_SnapshotM);
              }
              self.SaveProgressProvider.finish();
              resolve(self);
            })
            .catch(err => {
              self.SaveProgressProvider.exception(err);
              reject(self);
            })

        });
    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbSnapshotType = <IwbSnapshotType>self.drWB_SnapshotType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbSnapshotType.SNAPSHOT_TYPE_NAME + "(" + wbSnapshotType.SNAPSHOT_TYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Snapshot Type

            // 2. If Valid, mark all the records for deletion.
            self.drWB_SnapshotType.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Snapshot_Type, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    async addGLAccount(aSubjectAreaId: number): Promise<IBusinessObject> {
        return new Promise<IBusinessObject>(async (resolve, reject) => {
            const self = this;
            try {
                let aSubjectAreaRow: WB_SubjectAreaRow;
                const pSubjectAreaRow = <WB_SubjectAreaRow>await self.dbContext.find(esWB_Subject_Area, aSubjectAreaId);
                if (!pSubjectAreaRow) {
                    return;
                }
                aSubjectAreaRow = pSubjectAreaRow;
                const aSnapShotMSARRow = <WB_SnapshotTypeSubjectAreaRow>await self.dbContext.createRecordWithKey(esWB_Snapshot_Msar);
                if (!aSubjectAreaRow) {
                    resolve(self);
                    return;
                }
                aSnapShotMSARRow.setDefaults();
                aSnapShotMSARRow.cif.WB_SUBJECTAREA_ID = aSubjectAreaRow.cif.WB_SUBJECTAREA_ID;
                aSnapShotMSARRow.WB_SubjectAreaRow = aSubjectAreaRow;
                aSnapShotMSARRow.cif.WB_SNAPSHOTM_ID = self.WB_SnapshotType.cif.WB_SNAPSHOTM_ID;
                await self.dbContext.add(esWB_Snapshot_Msar, aSnapShotMSARRow);
                resolve(self);
            } catch (error) {
                console.error("An Error Occurred During Adding Row", error);
                reject(error);
            }
        });
    }

    removeSubjectArea(asubjectAreaId: number): Promise<SnapshotTypeBO> {
        const self = this

        return new Promise<SnapshotTypeBO>((resolve, error) => {
            _.forEach(self.drWB_SnapshotType.WB_SnapshotTypeSubjectAreaRows, (aSnapShotMSARRow: WB_SnapshotTypeSubjectAreaRow) => {
                if (aSnapShotMSARRow.isActive && aSnapShotMSARRow.cif.WB_SUBJECTAREA_ID === asubjectAreaId) {
                    aSnapShotMSARRow.markDeleted();
                }
            });
            resolve(self);
        });
    }
};
PAEnvironment.registerBusinessObjectClass( SnapshotTypeBO ,"SnapshotTypeBO", "Snapshot Type")